/**
 * <SectionPreview> — Aperçu live du rendu d'une section, affiché en haut du
 * bloc d'édition dans /admin/contenu. Lit les valeurs en cours d'édition
 * (pas encore enregistrées) depuis `values` et s'actualise en temps réel.
 *
 * Pattern inspiré de Sakinea : preview au-dessus de la grille de champs pour
 * que l'admin voie immédiatement l'effet de ses changements.
 */
import { assetUrl } from "@/lib/api";

/**
 * Aperçu live d'un hero (titre HTML + sous-titre + CTA + fond).
 *
 * Props :
 *   - prefix : préfixe des keys (ex: "home.hero.b2c", "home.hero.b2b", "cee.hero")
 *   - values : map { key → value } en cours d'édition
 *   - ctaPrimary, ctaSecondary, trust : keys absolues optionnelles pour les CTA & ligne de confiance
 */
export function HeroPreview({ prefix, values, ctaPrimary, ctaSecondary, trust }) {
    const get = (k) => values[k] ?? "";
    const title = get(`${prefix}.title`);
    const subtitle = get(`${prefix}.subtitle`);
    const eyebrow = get(`${prefix}.eyebrow`);
    const bgSrc = get(`${prefix}.bg`);
    const bgOpacity = parseFloat(values[`${prefix}.bg.opacity`] ?? "100");
    const bgBlur = parseFloat(values[`${prefix}.bg.blur`] ?? "0");
    const overlayColor = values[`${prefix}.bg.overlay_color`] || "#0A3B2A";
    const overlayOpacity = parseFloat(values[`${prefix}.bg.overlay_opacity`] ?? "0");

    const ctaPrim = ctaPrimary ? get(ctaPrimary) : "";
    const ctaSec = ctaSecondary ? get(ctaSecondary) : "";
    const trustLine = trust ? get(trust) : "";

    return (
        <div className="relative rounded-2xl overflow-hidden border-2 border-border bg-secondary" style={{ aspectRatio: "16 / 7" }}>
            {/* Background image */}
            {bgSrc ? (
                <>
                    <img
                        src={assetUrl(bgSrc)}
                        alt=""
                        aria-hidden="true"
                        className="absolute inset-0 w-full h-full object-cover"
                        style={{
                            opacity: (Number.isFinite(bgOpacity) ? bgOpacity : 100) / 100,
                            filter: bgBlur > 0 ? `blur(${bgBlur}px)` : undefined,
                        }}
                    />
                    {overlayOpacity > 0 && (
                        <div
                            className="absolute inset-0"
                            aria-hidden="true"
                            style={{ backgroundColor: overlayColor, opacity: overlayOpacity / 100 }}
                        />
                    )}
                </>
            ) : (
                <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-accent/10" aria-hidden="true" />
            )}

            {/* Content */}
            <div className="relative z-10 h-full flex flex-col items-start justify-center px-6 md:px-10 py-6">
                {eyebrow && (
                    <p className="text-[10px] md:text-xs uppercase tracking-[0.18em] font-semibold text-primary/80 mb-2">
                        {eyebrow}
                    </p>
                )}
                {title && (
                    <h2
                        className="font-display text-2xl md:text-4xl font-black text-primary leading-[1.1] max-w-[80%]"
                        dangerouslySetInnerHTML={{ __html: title }}
                    />
                )}
                {subtitle && (
                    <p className="text-xs md:text-sm text-foreground/80 mt-3 max-w-[60%] line-clamp-2">
                        {subtitle}
                    </p>
                )}
                <div className="flex gap-2 mt-4">
                    {ctaPrim && (
                        <span className="inline-flex items-center rounded-full bg-primary text-primary-foreground px-3 py-1.5 text-xs font-semibold">
                            {ctaPrim}
                        </span>
                    )}
                    {ctaSec && (
                        <span className="inline-flex items-center rounded-full border-2 border-primary/30 px-3 py-1.5 text-xs font-semibold text-primary bg-background/70">
                            {ctaSec}
                        </span>
                    )}
                </div>
                {trustLine && (
                    <p className="text-[10px] md:text-xs text-muted-foreground mt-3 line-clamp-1">
                        {trustLine}
                    </p>
                )}
            </div>

            {/* Badge "Aperçu live" */}
            <span className="absolute top-3 right-3 z-20 inline-flex items-center gap-1 rounded-full bg-background/85 backdrop-blur px-2.5 py-1 text-[10px] uppercase tracking-wider font-bold text-primary border border-border">
                <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" />
                Aperçu live
            </span>
        </div>
    );
}

/**
 * Aperçu live d'un bandeau CTA (titre + corps + bouton).
 * Utilisé pour home-cta-strip.
 */
export function CtaStripPreview({ prefix, values }) {
    const get = (k) => values[k] ?? "";
    const title = get(`${prefix}.title`);
    const body = get(`${prefix}.body`);
    const cta = get(`${prefix}.cta`);
    return (
        <div className="relative rounded-2xl overflow-hidden border-2 border-border bg-primary text-primary-foreground px-6 py-8 md:px-10 md:py-12">
            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] items-center gap-6">
                <div>
                    {title && (
                        <h3
                            className="font-display text-xl md:text-3xl font-bold tracking-tight"
                            dangerouslySetInnerHTML={{ __html: title }}
                        />
                    )}
                    {body && (
                        <p className="text-primary-foreground/80 text-xs md:text-sm mt-2 max-w-xl">
                            {body}
                        </p>
                    )}
                </div>
                {cta && (
                    <span className="inline-flex items-center rounded-full bg-accent text-accent-foreground px-4 py-2 text-xs font-bold shrink-0">
                        {cta}
                    </span>
                )}
            </div>
            <span className="absolute top-3 right-3 z-20 inline-flex items-center gap-1 rounded-full bg-background/85 backdrop-blur px-2.5 py-1 text-[10px] uppercase tracking-wider font-bold text-primary border border-border">
                <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" />
                Aperçu live
            </span>
        </div>
    );
}

/**
 * Aperçu live d'une bande de chiffres (stats).
 */
export function StatsPreview({ prefix, values, count = 4 }) {
    const get = (k) => values[k] ?? "";
    return (
        <div className="relative rounded-2xl overflow-hidden border-2 border-border bg-primary text-primary-foreground p-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {Array.from({ length: count }).map((_, i) => {
                    const idx = i + 1;
                    return (
                        <div key={idx}>
                            <p className="font-display text-2xl md:text-4xl font-black text-accent">
                                {get(`${prefix}.${idx}.value`) || "—"}
                            </p>
                            <p className="text-[11px] md:text-sm text-primary-foreground/80 mt-1 line-clamp-2">
                                {get(`${prefix}.${idx}.label`)}
                            </p>
                        </div>
                    );
                })}
            </div>
            <span className="absolute top-3 right-3 z-20 inline-flex items-center gap-1 rounded-full bg-background/85 backdrop-blur px-2.5 py-1 text-[10px] uppercase tracking-wider font-bold text-primary border border-border">
                <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" />
                Aperçu live
            </span>
        </div>
    );
}

/**
 * Routeur de preview : choisit le bon composant selon l'id de la section.
 * Retourne null si la section n'a pas de preview défini.
 */
export default function SectionPreviewRouter({ sectionId, values }) {
    switch (sectionId) {
        case "home-hero-b2c":
            return (
                <HeroPreview
                    prefix="home.hero.b2c"
                    values={values}
                    ctaPrimary="home.hero.cta_primary"
                    ctaSecondary="home.hero.cta_secondary"
                    trust="home.hero.trust"
                />
            );
        case "home-hero-b2b":
            return (
                <HeroPreview
                    prefix="home.hero.b2b"
                    values={values}
                    ctaPrimary="home.hero.cta_primary"
                    ctaSecondary="home.hero.cta_secondary"
                    trust="home.hero.trust"
                />
            );
        case "cee-solaire":
            return (
                <HeroPreview
                    prefix="cee.hero"
                    values={values}
                    ctaPrimary="cee.hero.cta_primary"
                    ctaSecondary="cee.hero.cta_secondary"
                    trust="cee.hero.trust"
                />
            );
        case "home-stats":
            return <StatsPreview prefix="home.stats" values={values} count={4} />;
        case "home-cta-strip":
            return <CtaStripPreview prefix="home.cta_strip" values={values} />;
        default:
            return null;
    }
}
